---
id: log-analysis
title: Log Pattern Analysis
category: monitoring
difficulty: beginner
tags:
  - logs
  - analysis
  - patterns
  - troubleshooting
  - errors
description: >
  Analyzes application logs to identify patterns, anomalies, and
  actionable insights.
whenToUse: >
  When investigating production issues, analyzing error spikes, or
  performing periodic log health checks.
variables:
  - name: logs
    desc: Log entries to analyze (50-500 lines)
  - name: service_name
    desc: Service that produced the logs
  - name: time_window
    desc: Time range the logs cover
  - name: known_issues
    desc: Any currently known issues (or "none")
tips:
  - Provide logs with timestamps intact — temporal ordering is crucial for causal analysis.
  - Include both error logs and surrounding context (info/debug) for better correlation.
  - Mention known issues to help the model distinguish new problems from existing ones.
---

You are a log analysis expert. Analyze these application logs and identify patterns.

## Log Sample
```
{{logs}}
```

## Context
- Service: {{service_name}}
- Time window: {{time_window}}
- Known issues: {{known_issues}}

## Task
1. **Pattern Recognition** — Group log entries by pattern (error type, source, frequency)
2. **Anomaly Detection** — Flag unusual entries that don't match normal patterns
3. **Root Cause Correlation** — Connect related log entries into causal chains
4. **Actionable Insights** — Provide specific recommendations

## Output Format (JSON)
{
  "patterns": [
    {
      "pattern": "description",
      "frequency": "count or rate",
      "severity": "critical | warning | info",
      "sample_entry": "representative log line",
      "likely_cause": "hypothesis"
    }
  ],
  "anomalies": [
    {
      "entry": "the unusual log line",
      "why_unusual": "explanation"
    }
  ],
  "causal_chain": "narrative connecting events",
  "recommendations": [
    { "action": "what to do", "priority": "P1 | P2 | P3" }
  ]
}

<!-- EXAMPLE_OUTPUT -->

{
  "patterns": [
    {
      "pattern": "Connection pool exhaustion",
      "frequency": "47 occurrences in 5 minutes",
      "severity": "critical",
      "sample_entry": "ERROR: pool timeout - no available connections after 30s",
      "likely_cause": "Database connection leak — connections acquired but not released on error path"
    }
  ],
  "causal_chain": "Slow query at 14:23:01 → connection hold time increases → pool fills by 14:23:45 → cascading timeouts → service degradation",
  "recommendations": [
    { "action": "Add connection pool metrics and set max lifetime", "priority": "P1" }
  ]
}
